import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, addDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { validateNoBadWords } from '../utils/badWords';
import { userHasActiveSupportTicket } from '../utils/supportTicketLimits';
import * as supportTicketEvents from '../utils/supportTicketEvents';
import './HelpFeedbackButtons.css';

const TEMPLATES = {
  bug: `Bug report

Steps to reproduce:
1. 

Expected:

Actual:

Device / browser:
`,
  suggestion: `Suggestion

Idea:

Why it would help:
`,
  feedback: `Feedback
`,
  other: `Message
`,
};

const CATEGORY = {
  bug: 'bug_report',
  suggestion: 'suggestion',
  feedback: 'feedback',
  other: 'other',
};

const HelpFeedbackButtons = () => {
  const [user] = useAuthState(auth);
  const [showModal, setShowModal] = useState(false);
  const [feedbackType, setFeedbackType] = useState('feedback');
  const [message, setMessage] = useState(TEMPLATES.feedback);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleTypeChange = (t) => {
    setFeedbackType(t);
    setMessage(TEMPLATES[t]);
  };

  const handleClose = () => {
    setShowModal(false);
    setMessage(TEMPLATES.feedback);
    setFeedbackType('feedback');
    setError('');
  };

  const submitFeedback = async () => {
    const body = message.trim();
    if (!body || !user) return;
    const profanity = validateNoBadWords(body);
    if (profanity) {
      setError(profanity);
      return;
    }
    setSending(true);
    setError('');
    try {
      const busy = await userHasActiveSupportTicket(user.uid);
      if (busy) {
        setError('You already have an open request. Wait for an admin reply or use My requests.');
        setSending(false);
        return;
      }
      const now = new Date().toISOString();
      const subj = { bug: 'Bug', suggestion: 'Suggestion', feedback: 'Feedback', other: 'Message' }[feedbackType] || 'Feedback';
      const ticketRef = await addDoc(collection(db, 'supportTickets'), {
        subject: `${subj} · Speedsplit`,
        category: CATEGORY[feedbackType] || 'feedback',
        status: 'open',
        authorUid: user.uid,
        authorEmail: user.email || '',
        authorName: user.displayName || user.email?.split('@')[0] || 'User',
        source: 'fab',
        createdAt: now,
        updatedAt: now,
      });
      await addDoc(collection(db, 'supportTickets', ticketRef.id, 'messages'), {
        body,
        authorRole: 'user',
        authorUid: user.uid,
        createdAt: now,
      });
      supportTicketEvents.dispatchSupportTicketCreated(ticketRef.id);
      handleClose();
    } catch {
      setError('Could not send. Try Contact Us.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="help-feedback-fab-group">
        {user && (
          <button
            className="fab fab-feedback"
            title="Report issue or feedback"
            onClick={() => setShowModal(true)}
            aria-label="Report issue or feedback"
          >
            <i className="bi bi-flag-fill"></i>
          </button>
        )}
        <Link
          className="fab fab-help"
          to="/contact-us"
          title="Contact support"
          aria-label="Contact support"
        >
          <i className="bi bi-envelope-fill"></i>
        </Link>
      </div>

      {showModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={handleClose}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg">
              <div
                className="modal-header text-white"
                style={{ background: 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))' }}
              >
                <h5 className="modal-title">
                  <i className="bi bi-chat-dots me-2"></i>
                  Report / Feedback
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={handleClose}></button>
              </div>

              <div className="modal-body p-4">
                <div className="mb-3">
                  <label className="form-label fw-semibold">Type</label>
                  <div className="d-flex gap-2 flex-wrap">
                    {[
                      { key: 'bug', label: 'Bug Report', icon: 'bi-bug' },
                      { key: 'suggestion', label: 'Suggestion', icon: 'bi-lightbulb' },
                      { key: 'feedback', label: 'Feedback', icon: 'bi-chat-dots' },
                      { key: 'other', label: 'Other', icon: 'bi-three-dots' },
                    ].map(({ key, label, icon }) => (
                      <button
                        key={key}
                        type="button"
                        className={`btn btn-sm ${feedbackType === key ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => handleTypeChange(key)}
                      >
                        <i className={`bi ${icon} me-1`}></i>{label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="feedbackMessage" className="form-label fw-semibold">
                    <i className="bi bi-pencil me-1"></i>Message
                  </label>
                  <textarea
                    id="feedbackMessage"
                    className="form-control"
                    rows="8"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                  ></textarea>
                  <small className="text-muted">Submit goes to our support inbox. Replies appear under My requests.</small>
                </div>

                {error && <div className="alert alert-danger py-2 small">{error}</div>}

                <div className="d-grid">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={submitFeedback}
                    disabled={!message.trim() || sending}
                  >
                    {sending ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    ) : (
                      <><i className="bi bi-send me-2"></i>Submit</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpFeedbackButtons;
